import Modal from '@mui/material/Modal'
import styled, { keyframes } from 'styled-components'
import { JotaMaterialClose, tokens } from '@JOTAJornalismo/jota-design-system'
import Image from 'next/image'
import { TagDS } from '../geral/JOTADS.components'
import { FC, useEffect, useState } from 'react'
import { CheckCircle } from '@mui/icons-material'
import { FormRDComponent } from './form-rd'
import { addDatalayer, generateNumericHash } from '@App/libs/utils/geral'
import {
    ModalI,
    VerticalName,
    VerticalNameActive,
    VerticalType
} from '@App/libs/interfaces/PRO.interfaces'
import { getColorVertical, getVertical } from '@App/libs/utils/pro'
import { MOBILE_SIZE } from '../lp/tributos/services.component'
import { StoreType } from '@App/api/types/store'
import { useSelector } from 'react-redux'
import { FormRD, INFO_PRO_URL } from '@App/libs/utils/constants/pro'
import { useRouter } from 'next/router'

const { colors, fonts, spaces } = tokens

export const CheckItemComponent: FC<{ text: string }> = ({ text }) => (
    <CheckItem>
        <CheckCircle data-testid='check-circle' />
        <CheckTitle data-testid='check-title'>{text}</CheckTitle>
    </CheckItem>
)

export const getFormLink = (type: VerticalType) => {
    switch (type) {
        case VerticalName.PODER:
            return FormRD.Poder
        case VerticalName.TRIBUTOS:
            return FormRD.Tributos
        case VerticalName.SAUDE:
            return FormRD.Saúde
        case VerticalName.TRABALHISTA:
            return FormRD.Trabalhista
        default:
            return VerticalName.INFO
    }
}

const IMAGES_PATH = '/images/pro-modal/'

const imageMapUpsell = {
    [VerticalName.PODER]: 'upsell-pro-poder.jpg',
    [VerticalName.TRIBUTOS]: 'upsell-pro-tributos.jpg',
    [VerticalName.SAUDE]: 'upsell-pro-saude.jpg',
    [VerticalName.TRABALHISTA]: 'upsell-pro-trabalhista.jpg',
    [VerticalName.INFO]: 'upsell-pro-poder.jpg'
}

const imageMapVertical = {
    [VerticalName.PODER]: 'conheca-pro-poder.jpg',
    [VerticalName.TRIBUTOS]: 'conheca-pro-tributos.jpg',
    [VerticalName.SAUDE]: 'conheca-pro-saude.jpg',
    [VerticalName.TRABALHISTA]: 'conheca-pro-poder.jpg',
    [VerticalName.INFO]: 'conheca-pro-poder.jpg'
}

export const getReverseImagePath = (
    type: VerticalNameActive,
    infoType: VerticalType | 'Alerta' = VerticalName.INFO
) => {
    if (infoType !== VerticalName.INFO) {
        return `${IMAGES_PATH}${imageMapUpsell[type as keyof typeof imageMapUpsell] || imageMapUpsell[VerticalName.PODER]}`
    } else {
        return `${IMAGES_PATH}${imageMapVertical[type as keyof typeof imageMapVertical] || imageMapVertical[VerticalName.PODER]}`
    }
}

export const getUserRole = (userProfile: any) => {
    const roleMap = {
        has_poder: VerticalName.PODER,
        has_tributos: VerticalName.TRIBUTOS,
        has_saude: VerticalName.SAUDE,
        has_trabalhista: VerticalName.TRABALHISTA
    }

    for (const key in roleMap) {
        if (
            Object.prototype.hasOwnProperty.call(roleMap, key) &&
            userProfile?.[key as keyof typeof roleMap]
        ) {
            return roleMap[key as keyof typeof roleMap]
        }
    }
    return VerticalName.INFO
}

export const ModalDS: FC<ModalI> = ({
    isOpen = false,
    vertical = VerticalName.PODER,
    bulletItems = {
        Poder: [],
        Tributos: [],
        Saúde: [],
        Energia: [],
        Trabalhista: [],
        Info: [],
        Alerta: []
    },
    closeModal
}) => {
    const { auth } = useSelector((state: StoreType) => state)
    const userBO: any = auth.user?.getUserBO()

    const [dataLayerFixedData, setDataLayerFixedData] = useState({})
    const router = useRouter()

    useEffect(() => {
        if (isOpen) {
            const dataTemp = {
                url: window.location.href,
                vertical: vertical.toLowerCase(),
                history_page: document.referrer,
                date: new Date().getTime(),
                title: document.title,
                user_company: userBO?.profile?.company_name,
                user_tier: userBO?.profile?.company_tier,
                user_company_other: userBO?.profile?.company_type_other,
                user_plan: userBO?.profile?.plan,
                user_vertical: userBO?.verticals,
                type: 'JU'
            }

            addDatalayer({
                event: 'PRO_MODAL_EVENT',
                ...dataTemp
            })

            setDataLayerFixedData(dataTemp)
        }
    }, [isOpen, userBO?.profile, userBO?.verticals, vertical])

    return (
        <Modal
            open={isOpen}
            onClose={closeModal}
            aria-labelledby='modal-modal-title'
            aria-describedby='modal-modal-description'
            id='modal-backdrop'
        >
            <ModalBody isOpen={isOpen} data-cy='modal-show' id='modal-body'>
                <ModalContainer>
                    <ImageContainer id='modal-image'>
                        <CloseIconContainerModal onClick={closeModal}>
                            <JotaMaterialClose
                                fill={colors.gray.jotaGrayDark}
                            />
                        </CloseIconContainerModal>
                        <ImageModal
                            src={getReverseImagePath(
                                vertical,
                                getUserRole(userBO?.profile)
                            )}
                            alt='JOTA PRO Banner'
                            width={480}
                            height={300}
                            priority={true}
                        />
                    </ImageContainer>

                    <ModalContent id='modal-content'>
                        <TagDS
                            LeftIcon={'jotaMaterialLock'}
                            fillIcone='jotaGray'
                            color='jotaBlack'
                            label={'Conteúdo Exclusivo'}
                            size='medium'
                            type='outline'
                        />

                        <ModalTitle>
                            Leve o{' '}
                            <TitleStrong
                                color={
                                    getColorVertical(vertical) ?? 'defaultColor'
                                }
                                data-cy='title-vertical'
                            >
                                {getVertical(vertical)}
                            </TitleStrong>{' '}
                            para a sua empresa
                        </ModalTitle>

                        <div data-cy='items-vertical'>
                            {bulletItems[vertical]?.map((text) => (
                                <CheckItemComponent
                                    key={generateNumericHash(text)}
                                    text={text}
                                />
                            ))}
                        </div>

                        <Disclaimer>
                            Procurando soluções para pessoas físicas?
                            <br />
                            <LinkConfira
                                onClick={() => {
                                    addDatalayer({
                                        event: 'modal_clique_conteudo_gratis',
                                        dataLayerFixedData
                                    })
                                    router.push(INFO_PRO_URL)
                                }}
                            >
                                Aproveite nossos conteúdos grátis!
                            </LinkConfira>
                        </Disclaimer>
                    </ModalContent>
                </ModalContainer>
                <ModalContainerForm id='modal-form'>
                    <ModalHeaderForm>
                        <FormTitle id='modal-title'>
                            Preencha o formulário para testar nossa solução
                            corporativa
                        </FormTitle>
                        <CloseIconContainerModal
                            onClick={closeModal}
                            id='modal-close'
                        >
                            <JotaMaterialClose
                                fill={colors.gray.jotaGrayDark}
                            />
                        </CloseIconContainerModal>
                    </ModalHeaderForm>
                    <FormRDComponent
                        url={getFormLink(vertical)}
                        type={vertical}
                    />
                </ModalContainerForm>
            </ModalBody>
        </Modal>
    )
}

const ImageModal = styled(Image)`
    @media only screen and (max-width: ${MOBILE_SIZE}) {
        width: 100% !important;
        height: auto !important;
    }
`

const fadeIn = keyframes`
  from {
    transform: translate(-50%, -100%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, -50%);
    opacity: 1;
  }
`

const fadeInMobile = keyframes`
from {
    transform: translate(0, -100%);
    opacity: 0;
  }
  to {
    transform: translate(0, 0);
    opacity: 1;
  }
`

const ModalBody = styled.div<{ isOpen: boolean }>`
    background: ${colors.white.jotaWhite};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-width: 960px;
    width: 100%;
    max-height: 780px;
    height: 100%;
    display: flex;
    flex-direction: row;
    margin: 24px;
    border-radius: 5px;

    animation: ${({ isOpen }) => (isOpen ? fadeIn : 'none')} 0.5s forwards;

    &:focus-visible {
        outline: none;
    }

    @media only screen and (max-width: 762px) {
        flex-direction: row;
        width: 100%;
        max-width: inherit;
        transform: translate(0, 0);
        animation: ${({ isOpen }) => (isOpen ? fadeInMobile : 'none')} 0.5s
            forwards;
        left: inherit;
        top: inherit;
        margin: 5%;
        width: calc(100% - 10%);
        height: 100%;
        max-height: 100%;
        overflow: auto;
    }

    @media only screen and (max-width: ${MOBILE_SIZE}) {
        flex-direction: column;
        width: 100%;
        max-width: inherit;
        transform: translate(0, 0);
        animation: ${({ isOpen }) => (isOpen ? fadeInMobile : 'none')} 0.5s
            forwards;
        left: inherit;
        top: inherit;
        margin: 5%;
        width: calc(100% - 10%);
        height: 100%;
        max-height: 100%;
        overflow: auto;
    }
`

const ModalContainer = styled.div`
    width: 50%;
    display: flex;
    flex-direction: column;
    border-radius: 5px;

    @media only screen and (max-width: 762px) {
        width: 50%;
    }

    @media only screen and (max-width: ${MOBILE_SIZE}) {
        width: 100%;
    }
`

const ImageContainer = styled.div`
    & > div {
        display: none;
    }

    @media only screen and (max-width: ${MOBILE_SIZE}) {
        & div {
            position: absolute;
            top: -10px !important;
            right: -10px !important;
        }
    }
`

const ModalContainerForm = styled.div`
    background: #e6e8ed;
    width: 50%;
    border-radius: 5px;
    padding: ${spaces.jotaSpacing24};

    @media only screen and (max-width: ${MOBILE_SIZE}) {
        width: auto;
    }
`

const ModalContent = styled.div`
    padding: 40px;
    height: 480px;

    @media only screen and (max-width: ${MOBILE_SIZE}) {
        height: inherit;
        padding: 24px 16px;
    }
`

const ModalTitle = styled.h2`
    color: ${colors.black.jotaBlack};
    font-family: ${fonts.jotaHeading04};
    font-size: 27px !important;
`

const TitleStrong = styled.strong<{ color: string }>`
    color: ${({ color }) => color} !important;
    font-size: 30px;
    text-decoration: underline;
`

const CheckItem = styled.div`
    margin: ${spaces.jotaSpacing8} 0;
    display: flex;
    flex-direction: row;
    align-items: center;
`
const CheckTitle = styled.span`
    color: ${colors.gray.jotaGrayDarker};
    font-family: ${fonts.jotaBodyLarge};
    margin-left: ${spaces.jotaSpacing16};
`
const Disclaimer = styled.p`
    margin-top: ${spaces.jotaSpacing24};
    font-size: 12px;
    font-weight: 700;
    color: ${colors.gray.jotaGrayDarker};
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;

    & a {
        font-weight: 700;
    }
`

const ModalHeaderForm = styled.div`
    display: flex;
    flex-direction: row;

    @media only screen and (max-width: ${MOBILE_SIZE}) {
        flex-direction: row;

        & div {
            display: none;
        }
    }
`

const FormTitle = styled.h2`
    color: ${colors.black.jotaBlack};
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: 32px;
    margin: 0;
    padding: 0;
`

const CloseIconContainerModal = styled.div`
    display: flex;
    justify-self: flex-end;
    cursor: pointer;
    width: 24px;
    height: 24px;
`

const LinkConfira = styled.a`
    &:hover {
        text-decoration: underline;
    }
`
